import React, { useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Input,
  Spinner,
  Button,
  Select,
  SimpleGrid,
  Avatar,
} from '@chakra-ui/react';
import { useData } from '../../data';
import { useNavigate } from 'react-router-dom';
import PaginationControls from '../PaginationControls';
import { IoLocationOutline } from 'react-icons/io5';
import { BsGlobe2 } from 'react-icons/bs';
import { FaArrowRight } from 'react-icons/fa';
import BrandButton from '../brandButton/BrandButton';
import COUNTRY_OPTIONS from '../../data/countries';

const TYPE_OPTIONS = ['VC', 'Angel', 'Accelerator', 'DFI', 'Grant'];
const STAGE_OPTIONS = ['Pre-seed', 'Seed', 'Series A', 'Growth'];

const FunderCard = ({ funder, onClick }) => {
  return (
    <Box
      onClick={onClick}
      cursor="pointer"
      bg="white"
      borderWidth="1px"
      borderColor="#E2E8F0"
      className="rounded-lg max-w-md"
      minH="220px"
      _hover={{ shadow: 'lg' }}
    >
      <Box className="flex items-center flex-wrap gap-4 mb-2 p-5">
        <Avatar rounded="lg" name={funder.name} className="rounded-sm" />
        <Box className="flex py-2 flex-col">
          <Text fontWeight="700">{funder.name || 'Untitled funder'}</Text>
          <Box className="flex text-sm items-center gap-1">
            <IoLocationOutline className="text-gray-800" />
            <Text>{funder.hq_country || 'Not specified'}</Text>
          </Box>
        </Box>
      </Box>
      <Box className="flex flex-col gap-2 mb-5 px-5">
        <Text className="text-gray-500 text-xs font-bold">STAGE</Text>
        <Box className="flex gap-2 flex-wrap">
          {(funder.stage_focus || [])?.map((stage, index) => (
            <Box
              key={index}
              className="text-sm rounded-lg border border-blue-300 px-2 py-1 w-fit text-blue-700 bg-blue-50 font-medium"
            >
              {stage}
            </Box>
          ))}
        </Box>
      </Box>

      <Box className="flex flex-col gap-2 mb-5 px-5">
        <Text className="text-gray-500 text-xs font-bold">SECTORS</Text>
        <Box className="flex gap-2 flex-wrap">
          {(funder.sector_focus || [])?.map((sector, index) => (
            <Box
              key={index}
              className="text-sm rounded-lg border border-green-300 px-2 py-1 w-fit text-green-700 bg-green-50 font-medium"
            >
              {sector}
            </Box>
          ))}
        </Box>
      </Box>

      <Box className="flex flex-col gap-2 mb-5 px-5">
        <Text className="text-gray-500 text-xs font-bold">GEOGRAPHY</Text>
        <Box className="flex gap-2">
          <BsGlobe2 />
          <Text className="text-sm text-gray-700 font-medium">
            {funder.geography_focus.join(', ') || 'Not specified'}
          </Text>
        </Box>
      </Box>

      <Box className="flex flex-col gap-2 mb-5 px-5">
        <Text className="text-gray-500 text-xs font-bold">CHECK SIZE</Text>
        <Box className="flex gap-2 flex-wrap">
          <Box className="text-lg rounded-lg border border-purple-300 px-2 py-1 w-fit text-purple-700 bg-purple-50 font-medium">
            {funder.cheque_min && funder.cheque_max
              ? `$${funder.cheque_min.toLocaleString()} - $${funder.cheque_max.toLocaleString()}`
              : `Unspecified`}
          </Box>
        </Box>
      </Box>

      <Box
        onClick={onClick}
        className="flex rounded-b-lg justify-between text-blue-900 bg-gray-100 items-center mt-auto py-2 px-4 border-t border-gray-200"
      >
        <Text className="text-blue-900 font-bold">View Full Profile</Text>
        <FaArrowRight />
      </Box>
    </Box>
  );
};

const FundersList = () => {
  const [pageNo, setPageNo] = useState(1);
  const [pageSize] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilterUI, setTypeFilterUI] = useState('');
  const [countryFilterUI, setCountryFilterUI] = useState('');
  const [stageFilterUI, setStageFilterUI] = useState('');
  const [sectorFilterUI, setSectorFilterUI] = useState('');
  const [appliedTypeFilter, setAppliedTypeFilter] = useState('');
  const [appliedCountryFilter, setAppliedCountryFilter] = useState('');
  const [appliedStageFilter, setAppliedStageFilter] = useState('');
  const [appliedSectorFilter, setAppliedSectorFilter] = useState('');
  const navigate = useNavigate();
  const { data: surveydata, isLoading: surveyDataIsLoading } =
    useData('survey');

  const queryParts = [`page_no=${pageNo}`, `page_size=${pageSize}`];
  if (searchQuery) {
    queryParts.push(`search=${encodeURIComponent(searchQuery)}`);
  }
  if (appliedTypeFilter) {
    queryParts.push(`type=${encodeURIComponent(appliedTypeFilter)}`);
  }
  if (appliedCountryFilter) {
    queryParts.push(`country=${encodeURIComponent(appliedCountryFilter)}`);
  }
  if (appliedStageFilter) {
    queryParts.push(`stage=${encodeURIComponent(appliedStageFilter)}`);
  }
  if (appliedSectorFilter) {
    queryParts.push(`sector=${encodeURIComponent(appliedSectorFilter)}`);
  }

  const queryKey = `funders?${queryParts.join('&')}`;
  const { data, isLoading } = useData(queryKey, 0, {}, 1, true);

  const funders = data?.data?.funders || data?.funders || [];
  const totalItems =
    data?.data?.totalItems || data?.totalItems || funders.length || 0;

  const applySearch = () => {
    setPageNo(1);
    setSearchQuery(searchTerm.trim());
    setAppliedTypeFilter(typeFilterUI);
    setAppliedCountryFilter(countryFilterUI);
    setAppliedStageFilter(stageFilterUI);
    setAppliedSectorFilter(sectorFilterUI);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setSearchQuery('');
    setTypeFilterUI('');
    setCountryFilterUI('');
    setStageFilterUI('');
    setSectorFilterUI('');
    setAppliedTypeFilter('');
    setAppliedCountryFilter('');
    setAppliedStageFilter('');
    setAppliedSectorFilter('');
    setPageNo(1);
  };

  return (
    <Box className="flex flex-col gap-4">
      <Box className="bg-white rounded-xl p-5 shadow-sm">
        <Text fontSize="xl" fontWeight="700" mb={4}>
          Find Funding Partners
        </Text>
        <Text color="gray.600" mb={4}>
          Browse investment firms actively seeking startups to fund
        </Text>
        <Flex flexDir={{ base: 'column', md: 'row' }} gap={3}>
          <Input
            placeholder="Search funders by name, focus, or sector"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') applySearch();
            }}
            flex={1}
          />
          <Select
            placeholder="Filter by type"
            value={typeFilterUI}
            onChange={(e) => setTypeFilterUI(e.target.value)}
            w={{ base: '100%', md: '220px' }}
          >
            {TYPE_OPTIONS.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </Select>
          <Select
            placeholder="Filter by country"
            value={countryFilterUI}
            onChange={(e) => setCountryFilterUI(e.target.value)}
            w={{ base: '100%', md: '220px' }}
          >
            {COUNTRY_OPTIONS.map((country) => (
              <option key={country} value={country}>
                {country}
              </option>
            ))}
          </Select>
          <Select
            placeholder="Filter by stage"
            value={stageFilterUI}
            onChange={(e) => setStageFilterUI(e.target.value)}
            w={{ base: '100%', md: '220px' }}
          >
            {STAGE_OPTIONS.map((stage) => (
              <option key={stage} value={stage}>
                {stage}
              </option>
            ))}
          </Select>
          <Select
            placeholder="Filter by sector"
            value={sectorFilterUI}
            onChange={(e) => setSectorFilterUI(e.target.value)}
            w={{ base: '100%', md: '220px' }}
          >
            {surveydata?.data?.categories?.map((sector, index) => (
              <option key={index} value={sector.title}>
                {sector.title}
              </option>
            ))}
          </Select>
        </Flex>
        <Flex gap={3} mt={4} flexWrap="wrap">
          <BrandButton
            fontSize="13px"
            color="#fff"
            bg="brand.primary"
            borderColor="#C7DFF0"
            colorScheme="blue"
            text="Apply"
            w="full"
            maxW="100px"
            onClick={applySearch}
            isDisabled={
              !searchTerm &&
              !typeFilterUI &&
              !countryFilterUI &&
              !stageFilterUI &&
              !sectorFilterUI
            }
          />
          <BrandButton
            fontSize="13px"
            text="Reset"
            w="fit-content"
            maxW="400px"
            onClick={resetFilters}
            variant="outline"
          />
        </Flex>
      </Box>

      <Box className="bg-white rounded-xl p-5 shadow-sm">
        {isLoading ? (
          <Flex justifyContent="center" alignItems="center" minH="220px">
            <Spinner size="lg" />
          </Flex>
        ) : (
          <>
            {funders.length > 0 ? (
              <>
                <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} gap={4}>
                  {funders.map((funder) => (
                    <FunderCard
                      navigate={navigate}
                      key={funder.id}
                      funder={funder}
                      onClick={() => navigate(`/funders/${funder.id}`)}
                    />
                  ))}
                </SimpleGrid>
                <Box className="mt-6">
                  <PaginationControls
                    currentPage={pageNo}
                    totalPages={Math.max(1, Math.ceil(totalItems / pageSize))}
                    onPageChange={(newPage) => setPageNo(newPage)}
                  />
                </Box>
              </>
            ) : (
              <Flex justifyContent="center" alignItems="center" minH="220px">
                <Text color="gray.600">No funders found</Text>
              </Flex>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default FundersList;
